import type { UserRole } from './auth.js';
import type { DateSpan } from './events.js';

/** Stored marks. Unknown is the absence of a row (ADR-021). */
export const AVAILABILITY_STATUSES = ['available', 'unavailable'] as const;
export type AvailabilityStatus = (typeof AVAILABILITY_STATUSES)[number];

export const AVAILABILITY_MARKS = ['unknown', 'available', 'unavailable'] as const;
export type AvailabilityMark = (typeof AVAILABILITY_MARKS)[number];

export const AVAILABILITY_BULK_MODES = ['available_except', 'only_available'] as const;
export type AvailabilityBulkMode = (typeof AVAILABILITY_BULK_MODES)[number];

/** Inclusive Chicago calendar-date window the API will write in one bulk call. */
export const AVAILABILITY_MAX_RANGE_DAYS = 400;

export function isAvailabilityStatus(value: unknown): value is AvailabilityStatus {
  return typeof value === 'string' && (AVAILABILITY_STATUSES as readonly string[]).includes(value);
}

export function isAvailabilityMark(value: unknown): value is AvailabilityMark {
  return typeof value === 'string' && (AVAILABILITY_MARKS as readonly string[]).includes(value);
}

export function isAvailabilityBulkMode(value: unknown): value is AvailabilityBulkMode {
  return (
    typeof value === 'string' && (AVAILABILITY_BULK_MODES as readonly string[]).includes(value)
  );
}

export function roleCanSetAvailability(role: UserRole): boolean {
  return role === 'admin' || role === 'equipment_admin' || role === 'staff';
}

export function roleCanViewOthersAvailability(role: UserRole): boolean {
  return role === 'admin';
}

export function roleCanExportEventsIcs(role: UserRole): boolean {
  return roleCanSetAvailability(role);
}

export function roleCanHoldMachineToken(role: UserRole): boolean {
  return roleCanSetAvailability(role);
}

export function nextAvailabilityMark(current: AvailabilityMark): AvailabilityMark {
  if (current === 'unknown') return 'available';
  if (current === 'available') return 'unavailable';
  return 'unknown';
}

export interface PublicAvailabilityDay {
  date: string;
  status: AvailabilityStatus;
}

export interface AvailabilityListResponse {
  userId: string;
  from: string;
  to: string;
  days: PublicAvailabilityDay[];
}

export interface AvailabilityWriteRequest {
  status: AvailabilityMark;
}

export interface AvailabilityBulkRequest {
  from: string;
  to: string;
  mode: AvailabilityBulkMode;
  exceptions?: string[];
}

export interface AvailabilityPerson {
  id: string;
  name: string;
  role: UserRole;
}

export interface AvailabilityPeopleResponse {
  users: AvailabilityPerson[];
}

export interface WeekendRosterDay {
  date: string;
  mark: AvailabilityMark;
}

export interface WeekendRosterPerson {
  userId: string;
  name: string;
  role: UserRole;
  days: WeekendRosterDay[];
}

export interface WeekendRosterResponse {
  window: DateSpan;
  people: WeekendRosterPerson[];
}

export interface PublicCalendarFeedToken {
  id: string;
  tokenPrefix: string;
  lastUsedAt: string | null;
  revokedAt: string | null;
  createdAt: string;
}

export interface CalendarFeedListResponse {
  token: PublicCalendarFeedToken | null;
}

export interface CalendarFeedCreateResponse {
  token: PublicCalendarFeedToken;
  /** Raw path secret. Shown once at mint; never stored or listed. */
  raw: string;
  /** Subscribe URL including the raw token. Shown once. */
  url: string;
}

export interface CalendarFeedResponse {
  token: PublicCalendarFeedToken;
}

export interface CalendarCell {
  date: string;
  inMonth: boolean;
  weekday: number;
}

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export function isCalendarDate(value: string): boolean {
  if (!DATE_RE.test(value)) return false;
  const parts = value.split('-').map(Number);
  const year = parts[0];
  const month = parts[1];
  const day = parts[2];
  if (year === undefined || month === undefined || day === undefined) return false;
  const iso = new Date(Date.UTC(year, month - 1, day)).toISOString().slice(0, 10);
  return iso === value;
}

export function addCalendarDays(ymd: string, days: number): string {
  const parts = ymd.split('-').map(Number);
  const year = parts[0];
  const month = parts[1];
  const day = parts[2];
  if (year === undefined || month === undefined || day === undefined) {
    throw new Error(`Invalid calendar date "${ymd}".`);
  }
  return new Date(Date.UTC(year, month - 1, day + days)).toISOString().slice(0, 10);
}

export function eachCalendarDate(from: string, to: string): string[] {
  if (to < from) return [];
  const dates: string[] = [];
  let current = from;
  while (current <= to) {
    dates.push(current);
    current = addCalendarDays(current, 1);
  }
  return dates;
}

export function calendarWeekday(ymd: string): number {
  const parts = ymd.split('-').map(Number);
  const year = parts[0];
  const month = parts[1];
  const day = parts[2];
  if (year === undefined || month === undefined || day === undefined) {
    throw new Error(`Invalid calendar date "${ymd}".`);
  }
  return new Date(Date.UTC(year, month - 1, day)).getUTCDay();
}

/** Friday, Saturday, or Sunday — the operational weekend (ADR-021). */
export function isOperationalWeekend(ymd: string): boolean {
  const weekday = calendarWeekday(ymd);
  return weekday === 0 || weekday === 5 || weekday === 6;
}

export function monthGrid(year: number, month: number): CalendarCell[] {
  const first = `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-01`;
  if (!isCalendarDate(first)) throw new Error(`Invalid month ${year}-${month}.`);
  const startWeekday = calendarWeekday(first);
  const gridStart = addCalendarDays(first, -startWeekday);
  const cells: CalendarCell[] = [];
  for (let i = 0; i < 42; i += 1) {
    const date = addCalendarDays(gridStart, i);
    const parts = date.split('-').map(Number);
    cells.push({
      date,
      inMonth: parts[1] === month,
      weekday: calendarWeekday(date),
    });
  }
  return cells;
}

export function shiftYearMonth(
  year: number,
  month: number,
  delta: number,
): { year: number; month: number } {
  const index = year * 12 + (month - 1) + delta;
  const nextYear = Math.floor(index / 12);
  const nextMonth = (index % 12) + 1;
  return { year: nextYear, month: nextMonth };
}

export function yearMonthFromDate(ymd: string): { year: number; month: number } {
  const parts = ymd.split('-').map(Number);
  const year = parts[0];
  const month = parts[1];
  if (year === undefined || month === undefined) {
    throw new Error(`Invalid calendar date "${ymd}".`);
  }
  return { year, month };
}

export function bulkAvailabilityWrites(
  from: string,
  to: string,
  mode: AvailabilityBulkMode,
  exceptions: readonly string[] = [],
): { date: string; status: AvailabilityStatus }[] {
  const exceptionSet = new Set(exceptions.filter((date) => date >= from && date <= to));
  const writes: { date: string; status: AvailabilityStatus }[] = [];
  for (const date of eachCalendarDate(from, to)) {
    const isException = exceptionSet.has(date);
    if (mode === 'available_except') {
      writes.push({ date, status: isException ? 'unavailable' : 'available' });
    } else {
      writes.push({ date, status: isException ? 'available' : 'unavailable' });
    }
  }
  return writes;
}

export const MONTH_LABELS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const;

export const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;
